// frontend/src/pages/admin/AdminAILogs.js
import React, { useState, useEffect } from 'react';
import { aiLogsApi } from '../../api/aiLogs';
import { 
    FaRobot, FaUser, FaCommentDots, FaTimes, FaSpinner, 
    FaChevronDown, FaChevronRight, FaTrash, FaDownload, FaCheckSquare, FaSquare 
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

function AdminAILogs() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI State
  const [expandedUserId, setExpandedUserId] = useState(null); // Which user stack is open
  const [selectedSessionIds, setSelectedSessionIds] = useState([]); // For bulk actions
  const [isProcessing, setIsProcessing] = useState(false);

  // Detail Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await aiLogsApi.getChatUsers();
      setUsers(data);
    } catch (err) {
      setError("Failed to load AI logs.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- SELECTION LOGIC ---

  const handleSelectUser = (userId, sessions) => {
      // Select ALL sessions for this user
      const sessionIds = sessions.map(s => s.session_id);
      
      setSelectedSessionIds(prev => {
          const isAllSelected = sessionIds.every(id => prev.includes(id));
          if (isAllSelected) {
              // Deselect all
              return prev.filter(id => !sessionIds.includes(id));
          } else {
              // Select all (merge unique)
              return [...new Set([...prev, ...sessionIds])];
          }
      });
  };

  const handleSelectSession = (sessionId) => {
      setSelectedSessionIds(prev => 
          prev.includes(sessionId) 
          ? prev.filter(id => id !== sessionId) 
          : [...prev, sessionId]
      );
  };

  const isUserSelected = (sessions) => {
      if (!sessions || sessions.length === 0) return false;
      return sessions.every(s => selectedSessionIds.includes(s.session_id));
  };

  // --- ACTIONS ---

  const handleBulkDelete = async () => {
      if (selectedSessionIds.length === 0) return;
      if (!window.confirm(`Permanently delete ${selectedSessionIds.length} chat sessions?`)) return;

      setIsProcessing(true);
      try {
          await aiLogsApi.deleteSessions(selectedSessionIds);
          alert("Sessions deleted successfully.");
          setSelectedSessionIds([]);
          fetchUsers(); // Refresh list
      } catch (err) {
          alert("Failed to delete sessions.");
      } finally {
          setIsProcessing(false);
      }
  };

  const handleDownload = async (userId, userName) => {
      setIsProcessing(true);
      try {
          const history = await aiLogsApi.getUserFullHistory(userId);
          
          // Create JSON Blob
          const blob = new Blob([JSON.stringify(history, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          
          // Trigger Download
          const link = document.createElement('a');
          link.href = url;
          link.download = `ChatHistory_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
      } catch (err) {
          alert("Failed to download history.");
      } finally {
          setIsProcessing(false);
      }
  };

  const handleViewMessages = async (sessionId) => {
    setIsModalOpen(true);
    setLoadingMessages(true);
    setSelectedMessages([]);
    try {
      const messages = await aiLogsApi.getMessagesBySession(sessionId);
      setSelectedMessages(messages);
    } catch (err) {
      alert("Failed to load messages.");
    } finally {
      setLoadingMessages(false);
    }
  };

  const getVisibleText = (text) => {
      if (!text) return "";
      let clean = text.replace(/\[IMAGE_CACHE\]:.*?$/s, '').trim();
      clean = clean.replace(/\[ID_CACHE\]:.*?$/s, '').trim();
      return clean;
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading AI Logs...</div>;

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FaRobot className="text-primary" /> AI Chat Log Review
        </h1>
        
        {selectedSessionIds.length > 0 && (
            <button 
                onClick={handleBulkDelete}
                disabled={isProcessing}
                className="bg-red-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 hover:bg-red-700 shadow-sm"
            >
                {isProcessing ? <FaSpinner className="animate-spin" /> : <FaTrash />} 
                Delete Selected ({selectedSessionIds.length})
            </button>
        )}
      </div>

      {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}

      {/* USERS STACK TABLE */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        {users.length === 0 ? (
            <div className="p-10 text-center text-gray-500">No chat history found.</div>
        ) : (
            <div className="divide-y divide-gray-100">
                {users.map(user => {
                    const isExpanded = expandedUserId === user.user_id;
                    const isSelected = isUserSelected(user.sessions);

                    return (
                        <div key={user.user_id} className="bg-white">
                            {/* USER HEADER ROW */}
                            <div className={`p-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}>
                                <div className="flex items-center gap-4 flex-grow">
                                    {/* Checkbox */}
                                    <button onClick={() => handleSelectUser(user.user_id, user.sessions)} className="text-gray-400 hover:text-primary">
                                        {isSelected ? <FaCheckSquare className="text-primary text-lg" /> : <FaSquare className="text-lg" />}
                                    </button>

                                    {/* Expand Toggle */}
                                    <button onClick={() => setExpandedUserId(isExpanded ? null : user.user_id)} className="text-gray-500 hover:text-gray-800">
                                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                    </button>

                                    {/* User Info */}
                                    <div>
                                        <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                            <FaUser className="text-gray-400 text-xs" /> {user.user_name}
                                        </h3>
                                        <p className="text-xs text-gray-500">{user.user_email}</p>
                                    </div>
                                </div>

                                {/* Stats & Actions */}
                                <div className="flex items-center gap-6">
                                    <div className="text-right hidden sm:block">
                                        <span className="block font-bold text-lg text-primary">{user.session_count}</span>
                                        <span className="text-[10px] text-gray-500 uppercase">Sessions</span>
                                    </div>
                                    <div className="text-right hidden sm:block">
                                        <span className="block text-sm text-gray-700">{new Date(user.last_activity).toLocaleDateString()}</span>
                                        <span className="text-[10px] text-gray-500 uppercase">Last Active</span>
                                    </div>
                                    
                                    <button 
                                        onClick={() => handleDownload(user.user_id, user.user_name)}
                                        disabled={isProcessing}
                                        className="text-blue-600 hover:bg-blue-100 p-2 rounded"
                                        title="Download All Logs for User"
                                    >
                                        <FaDownload />
                                    </button>
                                </div>
                            </div>

                            {/* EXPANDED SESSIONS LIST */}
                            {isExpanded && (
                                <div className="bg-gray-50 border-t border-gray-200 pl-12 pr-4 py-2">
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr className="text-gray-500 border-b border-gray-200">
                                                <th className="py-2 w-10"></th>
                                                <th className="py-2">Session ID</th>
                                                <th className="py-2">Date</th>
                                                <th className="py-2 text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {user.sessions.map(session => (
                                                <tr key={session.session_id} className="hover:bg-gray-100">
                                                    <td className="py-2">
                                                        <button onClick={() => handleSelectSession(session.session_id)}>
                                                            {selectedSessionIds.includes(session.session_id) ? <FaCheckSquare className="text-primary" /> : <FaSquare className="text-gray-300" />}
                                                        </button>
                                                    </td>
                                                    <td className="py-2 font-mono text-xs text-gray-600">{session.session_id}</td>
                                                    <td className="py-2 text-gray-600">{new Date(session.last_activity).toLocaleString()}</td>
                                                    <td className="py-2 text-right">
                                                        <button 
                                                            onClick={() => handleViewMessages(session.session_id)}
                                                            className="text-blue-600 hover:underline font-bold flex items-center justify-end gap-1"
                                                        >
                                                            <FaCommentDots /> View Chat
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        )}
      </div>

      {/* --- CONVERSATION MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-fadeInUp">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-bold text-gray-800">Conversation Details</h3>
              <button onClick={() => setIsModalOpen(false)}><FaTimes /></button>
            </div>
            
            <div className="flex-grow overflow-y-auto p-6 bg-gray-50 space-y-4">
              {loadingMessages ? (
                <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Messages...</div>
              ) : (
                selectedMessages.map((msg, index) => (
                  <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg p-3 shadow-sm ${msg.sender === 'user' ? 'bg-primary text-white' : 'bg-white text-gray-800'}`}>
                      <div className="flex items-center gap-2 mb-1 text-xs opacity-75">
                        <span className="font-bold capitalize">{msg.sender}</span>
                        <span>• {new Date(msg.timestamp).toLocaleTimeString()}</span>
                      </div>
                      <div className="ai-message-content text-sm">
                        <ReactMarkdown>
                            {getVisibleText(msg.text)}
                        </ReactMarkdown>
                      </div>
                      {msg.image_url && (
                          <div className="mt-2">
                              <a href={msg.image_url} target="_blank" rel="noopener noreferrer">
                                  <img src={msg.image_url} alt="User Upload" className="w-24 h-24 object-cover rounded border border-gray-300" />
                              </a>
                          </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAILogs;